/**
 * Client-side mirror of the backend's maintainability gate (go-live-protection §3a). The
 * server's liveGuard('site-write') ALLOWS an admin to edit a maintainable live client (the
 * edit lands on the preview branch). This flag lets the frontend MATCH that exactly — so we
 * surface the edit controls that now work and keep hidden the ones that still 403.
 *
 * MUST stay in lock-step with the server lists:
 *   netlify/functions/_lib/retire/protected.ts  (PROTECTED_SLUGS)
 *   netlify/functions/_lib/maintain.ts           (isMaintainableSlug = SLUG_SHAPE && !protected)
 * The guard test `test/live-maintain-sync.test.ts` asserts they can't drift.
 *
 * This is UX only — the real enforcement is server-side (every endpoint 403s via liveGuard).
 * A frontend bug here can only hide/show a control; it can never create a write hole.
 */

// Same list as retire/protected.ts (infra fixtures + live hand-built Cooper — never app-managed).
export const PROTECTED_SLUGS = ['main', 'master', '_template', '_proof', 'app-sandbox', 'james-cooper'] as const;

// Same shape as maintain.ts: lowercase letters/digits/hyphens, not leading-hyphen.
const SLUG_SHAPE = /^[a-z0-9][a-z0-9-]*$/;

/** Mirrors the server `isMaintainableSlug` — correct slug shape AND not a protected slug. */
export function isMaintainableSlug(slug: string): boolean {
  return SLUG_SHAPE.test(slug) && !(PROTECTED_SLUGS as readonly string[]).includes(slug);
}

/**
 * Whether an admin may maintain this (live) client through the app — EXACTLY the backend
 * `liveGuard('site-write')` allow-condition: admin + a maintainable (non-protected) slug.
 * Used to compute `liveBlocked = client.is_live && !canMaintainLive`, so the frontend neither
 * surfaces a control that would 403 (non-admin / protected slug) nor hides one that now works.
 */
export function canMaintainLive(client: { slug: string }, isAdmin: boolean): boolean {
  return isAdmin && isMaintainableSlug(client.slug);
}
